import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, Modal, Toast, StatusBadge, EmptyState } from "@/components/Layout";
import api, { soft } from "@/lib/api";
import Select from "@/components/Select";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";

export default function Planning() {
  const { has } = useAuth();
  const [emps, setEmps] = useState([]);
  const [active, setActive] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [selE, setSelE] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [state, setState] = useState({ plan: null, periods: [], campaign: null });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ start_date:"", end_date:"", observations:"" });
  const [msg, setMsg] = useState({ text: "", type: "info" });

  useEffect(() => {
    Promise.all([api.get("/planco/my-employments"),
                 soft(api.get("/planco/campaigns/active"), null), soft(api.get("/config"), null)])
      .then(([e,a,c]) => {
        setEmps(e.data); setActive(a.data); setCfg(c.data);
        if (a.data) setYear(a.data.planning_year);
        else if (c.data?.planning_year) setYear(c.data.planning_year);
        if (e.data[0]) setSelE(e.data[0].id);
      });
  }, []);

  const loadPlan = async () => {
    if (!selE) return;
    const r = await api.get(`/planco/plans/${selE}/${year}`);
    setState(r.data);
  };
  useEffect(() => { loadPlan(); }, [selE, year]);

  const empl = emps.find(e => e.id === selE);
  const totalBalance = empl ? (empl.co_year_available+empl.co_carried_previous+empl.co_extra_hazardous+empl.co_extra_other) : 0;
  const used = state.periods.reduce((s,p)=>s+p.days, 0);
  const remaining = totalBalance - used;

  const canEdit = useMemo(() => {
    if (!state.campaign) return false;
    const s = state.plan?.status;
    if (s === "submitted" || s === "approved") return false;
    const t = new Date().toISOString().slice(0,10);
    return state.campaign.status === "active" && t >= state.campaign.access_start && t <= state.campaign.access_end;
  }, [state]);

  const addPeriod = async () => {
    try { await api.post(`/planco/plans/${selE}/${year}/periods`, form);
      setAdding(false); setForm({start_date:"",end_date:"",observations:""}); loadPlan();
      setMsg({text:"Perioadă adăugată",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };
  const delPeriod = async (id) => { if(!window.confirm("Șterge perioada?")) return;
    await api.delete(`/planco/periods/${id}`); loadPlan(); };
  const submit = async () => {
    try { await api.post(`/planco/plans/${selE}/${year}/submit`); loadPlan();
      setMsg({text:"Trimis spre aprobare",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  // Highlight allowed periods for this employment's category
  const allowedForCategory = useMemo(() => {
    if (!state.campaign || !empl) return [];
    return (state.campaign.allowed_periods || []).filter(a => a.personnel_category === empl.personnel_category);
  }, [state.campaign, empl]);

  return (
    <>
      <PageHeader title="Programare Concediu" subtitle={state.campaign?.status === "active" ? `Campanie activă: ${state.campaign.name}` : (active ? "Fără campanie activă pentru acest raport de muncă" : "Fără campanie activă")} />
      <Toast msg={msg.text} type={msg.type} />
      {(!state.campaign || state.campaign.status !== "active") && <div className="card" style={{padding:16,marginBottom:16,borderLeft:"3px solid #F59E0B",background:"#FFFBEB"}}>
        ⚠️ Nu există campanie activă pentru categoria acestui raport de muncă. Programarea nu este posibilă.
      </div>}

      <div className="card" style={{padding:20,marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr 1fr",gap:16,alignItems:"end"}}>
          <div>
            <label className="label">Raport de muncă</label>
            <Select className="select" value={selE} onChange={e=>setSelE(e.target.value)} data-testid="plan-employment">
              {emps.map(e => (
                <option key={e.id} value={e.id}>{e.marca} · {e.user_name || ""}</option>
              ))}
            </Select>
          </div>
          <Stat label="Sold total" value={`${totalBalance}z`} />
          <Stat label="Programate" value={`${used}z`} />
          <Stat label="Disponibil" value={`${remaining}z`} danger={remaining < 0} />
        </div>
        {allowedForCategory.length > 0 && (
          <div style={{marginTop:14, padding:"8px 12px",background:"var(--sky-100)",borderRadius:6,fontSize:12,color:"var(--sky-700)"}}>
            <b>Perioade permise</b> pentru „{empl?.personnel_category}": {allowedForCategory.map((a,i)=>(
              <span key={i}>{a.start_date} → {a.end_date}{a.label?` (${a.label})`:""}{i<allowedForCategory.length-1?", ":""}</span>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{padding:"14px 20px",borderBottom:"1px solid #E2E8F0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontWeight:700,fontSize:15}}>Perioade programate</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:2,display:"flex",gap:8,alignItems:"center"}}>
              Status: <StatusBadge status={state.plan?.status || "draft"} />
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {canEdit && <button className="btn btn-secondary" onClick={()=>setAdding(true)} data-testid="add-period" {...lock(has("planning.manage_own"))}>+ Adaugă perioadă</button>}
            {canEdit && state.periods.length > 0 && <button className="btn btn-primary" onClick={submit} data-testid="submit-plan" {...lock(has("planning.manage_own"))}>Trimite spre aprobare</button>}
          </div>
        </div>
        <table className="tbl">
          <thead><tr><th>Perioada</th><th className="tabular-nums">Zile</th><th>Status</th><th>Observații</th><th></th></tr></thead>
          <tbody>{state.periods.map(p=>(
            <tr key={p.id}>
              <td style={{fontWeight:600}}>{p.start_date} → {p.end_date}</td>
              <td className="tabular-nums">{p.days}</td>
              <td><StatusBadge status={p.status} /></td>
              <td style={{color:"#64748B"}}>{p.observations || "—"}</td>
              <td>{canEdit && <button className="btn btn-danger" style={{padding:"3px 8px",fontSize:11}} onClick={()=>delPeriod(p.id)} {...lock(has("planning.manage_own"))}>Șterge</button>}</td>
            </tr>
          ))}</tbody>
        </table>
        {state.periods.length===0 && <EmptyState text="Nu ai programat încă nicio perioadă." />}
        {state.plan?.decision_comment && (
          <div style={{padding:"12px 20px",background:"#FEF3C7",borderTop:"1px solid #E2E8F0"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#92400E",textTransform:"uppercase"}}>Comentariu manager</div>
            <div style={{fontSize:13,color:"#334155",marginTop:2}}>{state.plan.decision_comment}</div>
          </div>
        )}
      </div>

      {adding && (
        <Modal title="Adaugă perioadă" onClose={()=>setAdding(false)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Data început</label>
              <input className="input" type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} data-testid="period-start" /></div>
            <div><label className="label label-req">Data sfârșit</label>
              <input className="input" type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})} data-testid="period-end" /></div>
            <div><label className="label">Observații</label>
              <textarea className="textarea" rows={3} value={form.observations} onChange={e=>setForm({...form,observations:e.target.value})} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setAdding(false)}>Anulează</button>
            <button className="btn btn-primary" onClick={addPeriod} data-testid="save-period" {...lock(has("planning.manage_own"))}>Adaugă</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Stat({ label, value, danger }) {
  return <div>
    <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.04em"}}>{label}</div>
    <div className="tabular-nums" style={{fontFamily:"Manrope",fontSize:22,fontWeight:800,color:danger?"#991B1B":"var(--navy)",marginTop:2}}>{value}</div>
  </div>;
}
